import { Badge, Box, Flex, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { Flag } from '@/components/Flag';
import { TeamDetailTrigger } from '@/components/teamDetail';
import { getTeamCategoryColorScheme, getTeamCategoryLabel } from '@/components/teamDetail/teamDetailUtils';
import type { AiModelContent } from '@/constants/aiLeague/aiLeagueTypes';
import { countriesInfo, countryCategory, tournamentCountriesInfo } from '@/models/country';
import { pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/LeaguePage/Card';
import {
  LEADERBOARD_ROW_MIN_H,
  LEADERBOARD_ROW_PY,
  LEADERBOARD_YOU_BORDER,
  LEADERBOARD_YOU_BORDER_COLOR,
} from '@/views/LeaguePage/LeaderboardRow';

const SWAPPED_ROW_BORDER_RADIUS = 'md';
/** Inset inside the green highlight; applied to every row so swapped borders and flags stay aligned. */
const SWAPPED_ROW_INSET = LEADERBOARD_ROW_PY;

function countListCategories(countryList: AiModelContent['countryList']) {
  let seeds = 0;
  let intermediates = 0;
  let outsiders = 0;
  for (const country of countryList) {
    const category = tournamentCountriesInfo[country]?.category;
    if (category === countryCategory.Favourite) seeds += 1;
    else if (category === countryCategory.Intermediate) intermediates += 1;
    else if (category === countryCategory.Outsider) outsiders += 1;
  }
  return { seeds, intermediates, outsiders };
}

export function AiLeagueRankedList(props: {
  countryList: AiModelContent['countryList'];
  listValid?: boolean;
  /** 0-based indices of rows to highlight (post-swap display list). */
  swappedRankIndices?: Set<number>;
}) {
  const nameFontSize = useBreakpointValue({ base: 14, sm: 15, md: 16 });
  const rankFontSize = useBreakpointValue({ base: 18, sm: 20, md: 21 });
  const flagW = useBreakpointValue({ base: 6, sm: 7, md: 8 });
  const counts = countListCategories(props.countryList);
  const listValid = props.listValid ?? true;
  const swapped = props.swappedRankIndices ?? new Set<number>();

  return (
    <VStack align='stretch' spacing={2} w='full'>
      {props.countryList.map((country, index) => {
        const isSwapped = swapped.has(index);

        return (
          <Box
            key={country}
            w='full'
            minH={LEADERBOARD_ROW_MIN_H}
            py={SWAPPED_ROW_INSET}
            pr={SWAPPED_ROW_INSET}
            border={LEADERBOARD_YOU_BORDER}
            borderColor={isSwapped ? LEADERBOARD_YOU_BORDER_COLOR : 'transparent'}
            borderRadius={SWAPPED_ROW_BORDER_RADIUS}
          >
            <TeamDetailTrigger
              country={country}
              source='profile-list'
              rank={index + 1}
              variant='row'
              w='full'
            >
              <Flex w='full' align='center' columnGap={3}>
                <Text fontWeight='semibold' fontSize={rankFontSize} w={8} flexShrink={0} textAlign='center'>
                  {index + 1}
                </Text>
                <Text flex={1} fontSize={nameFontSize} fontWeight='semibold' noOfLines={1} minW={0}>
                  {countriesInfo[country].name}
                </Text>
                <Badge
                  colorScheme={getTeamCategoryColorScheme(country)}
                  borderRadius='full'
                  px={2}
                  fontSize='xs'
                  flexShrink={0}
                >
                  {getTeamCategoryLabel(country)}
                </Badge>
                <Flex flexShrink={0} align='center' justify='center' minW={6}>
                  <Flag country={country} w={flagW} borderRadius='3px' svg />
                </Flex>
              </Flex>
            </TeamDetailTrigger>
          </Box>
        );
      })}
      <Text fontSize='xs' color={listValid ? 'gray.500' : 'orange.500'} pt={1}>
        {counts.seeds} seeds · {counts.intermediates} intermediates · {counts.outsiders} outsiders
        {!listValid ? ' · does not meet 4/4/2 rules' : ''}
      </Text>
    </VStack>
  );
}

export function AiLeagueListCard(props: {
  countryList: AiModelContent['countryList'];
  listValid?: boolean;
  swappedRankIndices?: Set<number>;
  /** Override card title (defaults to "Final 10-team list"). */
  title?: string;
}) {
  return (
    <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }} h='full'>
      <Text {...pageSectionTitleProps} mb={3}>
        {props.title ?? 'Final 10-team list'}
      </Text>
      <AiLeagueRankedList
        countryList={props.countryList}
        listValid={props.listValid}
        swappedRankIndices={props.swappedRankIndices}
      />
    </Card>
  );
}
